
import React, { useState, useRef } from 'react';
import Modal from './Modal';
import { ApiService } from '../services/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { Analytics } from '../services/analytics';
import { 
  Bug, 
  Lightbulb, 
  MessageSquare, 
  Loader2, 
  Send,
  Image as ImageIcon,
  X,
  Upload
} from 'lucide-react';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const { session } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [tab, setTab] = useState<'help' | 'feedback'>('help');
  const [type, setType] = useState<'bug' | 'feature'>('bug');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImage(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) {
      addToast("Please fill in all fields", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      await ApiService.submitFeedback({
        type,
        subject,
        description,
        email: session?.user?.email || 'anonymous',
        image: image || undefined
      });
      addToast(type === 'bug' ? "Issue reported. Thank you!" : "Feature request sent!", "success");
      handleClose();
    } catch (error) {
      addToast("Failed to submit feedback", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset form
    setTab('help');
    setSubject('');
    setDescription('');
    setType('bug');
    setImage(null);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Help & Feedback"
      maxWidth="max-w-lg"
    >
      <div className="p-6">

        {/* Primary Tabs */}
        <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg mb-6">
          <button
            type="button"
            onClick={() => { setTab('help'); Analytics.track('help_opened'); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-bold rounded-md transition-all ${
              tab === 'help'
                ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
          >
            <MessageSquare size={16} /> Quick Help
          </button>
          <button
            type="button"
            onClick={() => { setTab('feedback'); Analytics.track('feedback_opened'); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-bold rounded-md transition-all ${
              tab === 'feedback'
                ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
          >
            <Bug size={16} /> Feedback
          </button>
        </div>

        {tab === 'help' && (
          <div className="space-y-4">
            <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              <h4 className="font-bold text-sm text-slate-900 dark:text-white">Getting started</h4>
              <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                Not sure what to do first? Open the setup checklist.
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    Analytics.track('help_open_setup_checklist');
                    handleClose();
                    navigate('/?onboarding=1');
                  }}
                  className="px-3 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-xs font-bold transition-colors"
                >
                  Open setup checklist
                </button>
                <button
                  type="button"
                  onClick={() => {
                    Analytics.track('help_open_tickets_upload');
                    handleClose();
                    navigate('/tickets?upload=1');
                  }}
                  className="px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-200 text-white dark:text-slate-900 text-xs font-bold transition-colors"
                >
                  Upload a PCN
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => { Analytics.track('help_nav', { to: 'vehicles' }); handleClose(); navigate('/vehicles?new=1'); }}
                className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-left"
              >
                <div className="flex items-center gap-2 text-slate-900 dark:text-white font-bold text-sm"><span>Add vehicle</span></div>
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">Create and tag your fleet.</p>
              </button>

              <button
                type="button"
                onClick={() => { Analytics.track('help_nav', { to: 'hires' }); handleClose(); navigate('/hires?new=1'); }}
                className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-left"
              >
                <div className="flex items-center gap-2 text-slate-900 dark:text-white font-bold text-sm"><span>Create hire</span></div>
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">Link driver, vehicle, and dates.</p>
              </button>

              <button
                type="button"
                onClick={() => { Analytics.track('help_nav', { to: 'tickets' }); handleClose(); navigate('/tickets'); }}
                className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-left"
              >
                <div className="flex items-center gap-2 text-slate-900 dark:text-white font-bold text-sm"><span>Assign tickets</span></div>
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">Match tickets to hires for liability.</p>
              </button>

              <button
                type="button"
                onClick={() => { setTab('feedback'); setType('bug'); }}
                className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-left"
              >
                <div className="flex items-center gap-2 text-slate-900 dark:text-white font-bold text-sm"><span>Report an issue</span></div>
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">Attach a screenshot and steps.</p>
              </button>
            </div>

            <div className="text-xs text-slate-500 dark:text-slate-400">
              Tip: The “Ask FleetMind” button (bottom-right) can explain any screen.
            </div>
          </div>
        )}
        
        {tab === 'feedback' && (
          <div className="space-y-4">
        {/* Type Toggle */}
        <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
          <button
            type="button"
            onClick={() => setType('bug')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md transition-all ${
              type === 'bug' 
                ? 'bg-white dark:bg-slate-900 text-red-600 dark:text-red-400 shadow-sm' 
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
          >
            <Bug size={16} /> Report Issue
          </button>
          <button
            type="button"
            onClick={() => setType('feature')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md transition-all ${
              type === 'feature' 
                ? 'bg-white dark:bg-slate-900 text-purple-600 dark:text-purple-400 shadow-sm' 
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
          >
            <Lightbulb size={16} /> Request Feature
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          
          <div className={`p-4 rounded-lg border flex items-start gap-3 ${
            type === 'bug' ? 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-900/40' : 'bg-purple-50 dark:bg-purple-900/20 border-purple-100 dark:border-purple-900/40'
          }`}>
            <div className={`mt-0.5 ${type === 'bug' ? 'text-red-600 dark:text-red-400' : 'text-purple-600 dark:text-purple-400'}`}>
              {type === 'bug' ? <Bug size={20} /> : <Lightbulb size={20} />}
            </div>
            <div>
              <h4 className={`font-bold text-sm ${type === 'bug' ? 'text-red-900 dark:text-red-200' : 'text-purple-900 dark:text-purple-200'}`}>
                {type === 'bug' ? "Found a bug?" : "Have an idea?"}
              </h4>
              <p className={`text-xs ${type === 'bug' ? 'text-red-700 dark:text-red-200/90' : 'text-purple-700 dark:text-purple-200/90'} mt-1`}>
                {type === 'bug' 
                  ? "Let us know what's broken. We'll fix it as soon as possible." 
                  : "We're constantly improving. Tell us what would make your life easier."}
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-950 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500"
              placeholder={type === 'bug' ? "e.g., Cannot upload PDF..." : "e.g., Add integration with..."}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none min-h-[120px] resize-none bg-white dark:bg-slate-950 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500"
              placeholder={type === 'bug' ? "Steps to reproduce..." : "Explain how this feature would help you..."}
              required
            />
          </div>

          {/* Image Upload Area */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Attachment (Optional)
            </label>
            <input 
              type="file" 
              accept="image/*"
              className="hidden"
              ref={fileInputRef}
              onChange={handleImageChange}
            />
            
            {image ? (
              <div className="flex items-center justify-between p-3 border border-slate-200 dark:border-slate-800 rounded-lg bg-slate-50 dark:bg-slate-950/40">
                <div className="flex items-center gap-2 overflow-hidden">
                  <ImageIcon size={16} className="text-slate-400 shrink-0" />
                  <span className="text-sm text-slate-600 dark:text-slate-300 truncate">{image.name}</span>
                </div>
                <button
                  type="button"
                  onClick={() => { setImage(null); if(fileInputRef.current) fileInputRef.current.value = ''; }}
                  className="text-slate-400 hover:text-red-500 p-1 rounded-full hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-3 px-4 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-lg text-slate-500 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 hover:border-primary-400 hover:bg-slate-50 dark:hover:bg-slate-900/30 transition-all flex items-center justify-center gap-2"
              >
                <Upload size={16} />
                <span className="text-sm font-medium">Upload Screenshot</span>
              </button>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm font-medium mr-2 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-6 py-2 text-white rounded-lg text-sm font-bold flex items-center gap-2 transition-colors ${
                type === 'bug' ? 'bg-red-600 hover:bg-red-700' : 'bg-purple-600 hover:bg-purple-700'
              }`}
            >
              {isSubmitting ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
              {type === 'bug' ? 'Submit Report' : 'Send Request'}
            </button>
          </div>
        </form>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default FeedbackModal;
